import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

export type AppMode = 'demo' | 'live';

interface ModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  isDemo: boolean;
  isLive: boolean;
  isDemoAllowed: boolean;
  toggleMode: () => void;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

const MODE_STORAGE_KEY = 'app-mode';

interface ModeProviderProps {
  children: ReactNode;
  defaultMode?: AppMode;
  allowDemoMode?: boolean;
}

export function ModeProvider({ children, defaultMode = 'live', allowDemoMode = false }: ModeProviderProps) {
  const [mode, setModeState] = useState<AppMode>(() => {
    if (!allowDemoMode) {
      try { localStorage.removeItem(MODE_STORAGE_KEY); } catch (_) { /* ignore */ }
      return 'live';
    }
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      return (stored === 'demo' || stored === 'live') ? stored : defaultMode;
    } catch (error) {
      console.error('Failed to read mode from localStorage:', error);
      return defaultMode;
    }
  });

  // Persist mode changes to localStorage only when demo is allowed
  useEffect(() => {
    if (!allowDemoMode) {
      try { localStorage.removeItem(MODE_STORAGE_KEY); } catch (_) { /* ignore */ }
      return;
    }
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch (error) {
      console.error('Failed to save mode to localStorage:', error);
    }
  }, [mode, allowDemoMode]);

  const setMode = allowDemoMode
    ? (newMode: AppMode) => { setModeState(newMode); }
    : () => { /* no-op when demo not allowed */ };

  const toggleMode = allowDemoMode
    ? () => { setModeState((prev) => (prev === 'demo' ? 'live' : 'demo')); }
    : () => { /* no-op when demo not allowed */ };

  const value: ModeContextType = {
    mode: allowDemoMode ? mode : 'live',
    setMode,
    isDemo: allowDemoMode ? mode === 'demo' : false,
    isLive: allowDemoMode ? mode === 'live' : true,
    isDemoAllowed: allowDemoMode,
    toggleMode,
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode() {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}
